import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

import './owner-disconnected-modal.css';

// CC_DISCONNECT_VISIBILITY (2026-06-07, Symptom B): the peer-facing
// mirror of PeerLeftModal's owner-side notice. When the lobby OWNER's
// socket drops mid-game the backend GROUP-broadcasts
// OwnerDisconnectedMidGame (group_router.notify_peers_owner_
// disconnected_mid_game) and GameGroupHandler dispatches a window
// 'owner-disconnected-mid-game-event'. Previously a host drop was
// silent: peers sat on a frozen board and were "eventually forced to
// the main menu for no reason." This modal EXPLAINS the silence.
//
// The seat is patient — the owner may reconnect — so the default
// affordance keeps the player in the game (Wait), with an explicit
// Leave to Menu for a peer who would rather bail than wait (the
// explained exit). A running clock shows how long the host has been
// gone so the wait isn't open-ended-feeling. No owner gate is needed:
// the backend addresses only the remaining peers (the owner is the one
// who dropped), so any client that receives the event is, by
// construction, a peer.

// MICHAEL-OWNS-COPY: placeholder strings only. COPY PLACEHOLDER — Michael
// will overwrite the title / body / timer-label / button labels.
const TITLE = (displayName) => `${displayName} lost connection`;
const BODY = 'The host lost connection. The game is waiting for them to'
    + ' reconnect — hang tight.';
const TIMER_LABEL = 'Disconnected for';
const WAIT_LABEL = 'Wait';
const LEAVE_LABEL = 'Leave to Menu';


function _formatElapsed(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}


export default function OwnerDisconnectedModal({ app }) {
    const [state, setState] = useState({ show: false, displayName: '' });
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const handler = (event) => {
            const detail = (event && event.detail) || {};
            setState({
                show: true,
                displayName: detail.display_name || 'The host',
            });
            // Restart the clock for each fresh host drop (a reconnect
            // then a later drop is a new wait, not a continuation).
            setElapsed(0);
        };
        window.addEventListener(
            'owner-disconnected-mid-game-event', handler);
        return () => {
            window.removeEventListener(
                'owner-disconnected-mid-game-event', handler);
        };
    }, []);

    // Elapsed-time clock — a once-per-second UI tick, NOT data polling.
    // The "no setInterval" rule (HSH_STYLE) targets data refresh /
    // polling (use events); a wall-clock display has no event source and
    // is the canonical exception. Only runs while the modal is shown;
    // cleared on dismiss. Clearing an interval is not a persistent UI
    // side-effect, so this is StrictMode-safe.
    useEffect(() => {
        if (!state.show) return undefined;
        const intervalId = setInterval(() => {
            setElapsed((previous) => previous + 1);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [state.show]);

    const dismiss = () => setState({ show: false, displayName: '' });

    const onWait = () => dismiss();

    // Explained exit: the peer leaves the game group (patient peer-leave
    // on the backend) and routes to the Main Menu, mirroring
    // LobbyDestroyedModal's Back-to-lobby. Safe when app.game is absent.
    const onLeave = () => {
        dismiss();
        if (app && app.game && app.game.groupHandler) {
            app.game.groupHandler.leaveGameGroup();
        }
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    };

    if (!state.show) return null;

    return (
        <Modal
            show
            onHide={onWait}
            backdrop="static"
            keyboard={false}
            centered
            className="owner-disconnected-modal"
        >
            <Modal.Header>
                <Modal.Title>{TITLE(state.displayName)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{BODY}</p>
                <p className="owner-disconnected-modal-timer">
                    {TIMER_LABEL}{' '}
                    <span className="owner-disconnected-modal-elapsed">
                        {_formatElapsed(elapsed)}
                    </span>
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="seed" onClick={onWait}>
                    {WAIT_LABEL}
                </Button>
                <Button variant="dread" onClick={onLeave}>
                    {LEAVE_LABEL}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
